import {
  CalendarDateTime,
  fromDate,
  today,
  toZoned,
  ZonedDateTime,
} from '@internationalized/date'
import { AriaTimeFieldProps, useTimeField } from '@react-aria/datepicker'
import { useLocale } from '@react-aria/i18n'
import { useTimeFieldState } from '@react-stately/datepicker'
import { TimeValue } from '@react-types/datepicker'
import { theme } from '@traba/theme'
import { useRef } from 'react'
import styled from 'styled-components'

import { DateSegment } from './DateField'

const StyledTimeFieldDiv = styled.div<{
  isInvalid?: boolean
  isReadOnly?: boolean
}>`
  display: inline-flex;
  border: ${({ isReadOnly }) => (isReadOnly ? '0' : '1px')} solid
    ${({ isInvalid }) => (isInvalid ? theme.colors.Red50 : theme.colors.Grey30)};
  border-radius: 5px;
  .group:hover & {
    border-color: ${({ isInvalid }) =>
      isInvalid ? theme.colors.red : theme.colors.Grey50};
  }
  .group:focus-within & {
    border-color: ${({ isInvalid }) =>
      isInvalid ? theme.colors.red : theme.colors.Violet70};
  }
  padding-left: ${theme.space.xxxs}px;
  padding-right: ${theme.space.xxs}px;
  width: min-content;
`

interface TimeFieldProps extends AriaTimeFieldProps<TimeValue> {
  time?: Date | null
  timezone?: string
  setTime?: (newTime: Date | null) => void
  minTime?: Date
  maxTime?: Date
  disabled?: boolean
}

export function TimeField(props: TimeFieldProps) {
  const { locale } = useLocale()

  const handleDateChange = (date: TimeValue) => {
    if (props.setTime === undefined || props.disabled) {
      return
    }
    if (!date) {
      props.setTime(null)
      return
    }
    if (date instanceof ZonedDateTime) {
      props.setTime(date.toDate())
      return
    }
    // date is a Time (no timezone). Build a ZonedDateTime using the timezone
    // prop so we can convert to a JS Date. The date component (today) is
    // irrelevant for time-only fields; only the hour/minute matter.
    const tz =
      props.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone
    const todayInTz = today(tz)
    const calDateTime = new CalendarDateTime(
      todayInTz.year,
      todayInTz.month,
      todayInTz.day,
      date.hour,
      date.minute,
      date.second ?? 0,
    )
    props.setTime(toZoned(calDateTime, tz).toDate())
  }

  const modifiedProps = {
    ...props,
    value:
      props.value !== undefined
        ? props.value
        : props.time !== undefined && props.timezone
          ? props.time !== null
            ? fromDate(props.time, props.timezone)
            : null
          : undefined,
    ...(props.setTime !== undefined ? { onChange: handleDateChange } : {}),
    minValue:
      !!props.minTime && props.timezone
        ? fromDate(props.minTime, props.timezone)
        : undefined,
    maxValue:
      !!props.maxTime && props.timezone
        ? fromDate(props.maxTime, props.timezone)
        : undefined,
  }
  const state = useTimeFieldState({
    ...modifiedProps,
    locale,
  })

  const ref = useRef(null)
  const { labelProps, fieldProps, isInvalid } = useTimeField(props, state, ref)

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }} className="group">
      <label {...labelProps}>{props.label}</label>
      <StyledTimeFieldDiv
        {...fieldProps}
        isInvalid={isInvalid}
        isReadOnly={state.isReadOnly}
        ref={ref}
      >
        {state.segments.map((segment, i) => (
          <DateSegment key={i} segment={segment} state={state} />
        ))}
      </StyledTimeFieldDiv>
    </div>
  )
}
